/**
 * Daikin Scenarist Script (.sst) writer for DVD image-based subtitle export.
 * Encoding matches MacCaption: UTF-16-BE with BOM, CRLF, 4-color conversion table.
 */

import { getImageFileName } from "./generateFcpXml.js";

export const SCENARIST_DEFAULT_PLANES = {
    BG: [255, 255, 255],
    PA: [0, 0, 0],
    E1: [0, 0, 255],
    E2: [255, 0, 0],
};

const NEAR_PLANE_THRESHOLD = 48;
const HEADER_VALUE_COLUMN = 14;

export function parseHexColor(hex, fallback = [0, 0, 0]) {
    const raw = String(hex || "").trim().replace("#", "");
    if (!/^[0-9a-fA-F]{6}$/.test(raw)) {
        return fallback.slice();
    }
    return [
        parseInt(raw.slice(0, 2), 16),
        parseInt(raw.slice(2, 4), 16),
        parseInt(raw.slice(4, 6), 16),
    ];
}

export function rgbToHex(rgb) {
    return `#${rgb
        .map((channel) => Math.max(0, Math.min(255, channel)).toString(16).padStart(2, "0"))
        .join("")}`;
}

function colorDistance(a, b) {
    const dr = a[0] - b[0];
    const dg = a[1] - b[1];
    const db = a[2] - b[2];
    return Math.sqrt(dr * dr + dg * dg + db * db);
}

export function resolveScenaristPlanes(fillHex, outlineHex) {
    const BG = SCENARIST_DEFAULT_PLANES.BG.slice();
    let PA = parseHexColor(fillHex, SCENARIST_DEFAULT_PLANES.PA);
    let E1 = parseHexColor(outlineHex, SCENARIST_DEFAULT_PLANES.E1);
    let E2 = SCENARIST_DEFAULT_PLANES.E2.slice();

    if (colorDistance(PA, BG) < NEAR_PLANE_THRESHOLD) {
        PA = SCENARIST_DEFAULT_PLANES.PA.slice();
    }
    if (
        colorDistance(E1, BG) < NEAR_PLANE_THRESHOLD ||
        colorDistance(E1, PA) < NEAR_PLANE_THRESHOLD
    ) {
        E1 = SCENARIST_DEFAULT_PLANES.E1.slice();
        if (colorDistance(E1, PA) < NEAR_PLANE_THRESHOLD) {
            E1 = [0, 255, 0];
        }
    }
    if (
        colorDistance(E2, BG) < NEAR_PLANE_THRESHOLD ||
        colorDistance(E2, PA) < NEAR_PLANE_THRESHOLD ||
        colorDistance(E2, E1) < NEAR_PLANE_THRESHOLD
    ) {
        E2 = [255, 0, 255];
        if (
            colorDistance(E2, BG) < NEAR_PLANE_THRESHOLD ||
            colorDistance(E2, PA) < NEAR_PLANE_THRESHOLD ||
            colorDistance(E2, E1) < NEAR_PLANE_THRESHOLD
        ) {
            E2 = [0, 255, 255];
        }
    }

    return { BG, PA, E1, E2 };
}

export function getScriptStem(scriptName) {
    const name = String(scriptName || "untitled").trim() || "untitled";
    const lastSep = Math.max(name.lastIndexOf("/"), name.lastIndexOf("\\"));
    const base = lastSep >= 0 ? name.slice(lastSep + 1) : name;
    const lastDot = base.lastIndexOf(".");
    return lastDot > 0 ? base.slice(0, lastDot) : base;
}

export function ensureScriptExtension(scriptName, extension) {
    const ext = String(extension || "").replace(/^\./, "") || "xml";
    const name = String(scriptName || "untitled").trim() || "untitled";
    const lastSep = Math.max(name.lastIndexOf("/"), name.lastIndexOf("\\"));
    const lastDot = name.lastIndexOf(".");
    if (lastDot > lastSep) {
        return `${name.slice(0, lastDot)}.${ext}`;
    }
    return `${name}.${ext}`;
}

export function getScenaristSdResolution(currentId, frameRate) {
    if (currentId === "ntsc_sd" || currentId === "pal_sd") {
        return currentId;
    }
    const fps = parseFloat(frameRate);
    return fps === 25 || fps === 50 ? "pal_sd" : "ntsc_sd";
}

export function getImageNaming(profile) {
    if (profile === "scenarist") {
        return { extension: "tif", separator: "." };
    }
    return { extension: "png", separator: "" };
}

export function encodeUtf16Be(text) {
    const le = Buffer.from(String(text ?? ""), "utf16le");
    const be = Buffer.alloc(le.length + 2);
    be[0] = 0xfe;
    be[1] = 0xff;
    for (let i = 0; i < le.length; i += 2) {
        be[i + 2] = le[i + 1];
        be[i + 3] = le[i];
    }
    return be;
}

export function decodeUtf16Be(buffer) {
    const bytes = Buffer.from(buffer);
    let offset = 0;
    if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
        offset = 2;
    }
    const le = Buffer.alloc(bytes.length - offset);
    for (let i = 0; i < le.length; i += 2) {
        le[i] = bytes[offset + i + 1];
        le[i + 1] = bytes[offset + i];
    }
    return le.toString("utf16le");
}

function headerField(key, value) {
    const pad = Math.max(1, HEADER_VALUE_COLUMN - key.length);
    return `${key}${" ".repeat(pad)}${value}`;
}

function conversionLine(key, rgb) {
    return headerField(key, `(${rgb[0]} ${rgb[1]} ${rgb[2]}  = = = )`);
}

function formatCreated(date) {
    const created = date instanceof Date ? date : new Date(date);
    const month = created.getMonth() + 1;
    const day = created.getDate();
    const year = created.getFullYear();
    const hours = String(created.getHours()).padStart(2, "0");
    const minutes = String(created.getMinutes()).padStart(2, "0");
    const seconds = String(created.getSeconds()).padStart(2, "0");
    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
}

function displayGeometry(resolutionId) {
    if (resolutionId === "pal_sd") {
        return {
            tvType: "PAL",
            pixelArea: "(2 574)",
            displayArea: "(0 2 719 574)",
        };
    }
    return {
        tvType: "NTSC",
        pixelArea: "(2 479)",
        displayArea: "(0 2 719 479)",
    };
}

function formatEventRow(index, startTc, endTc, fileName) {
    const sp = String(index + 1).padStart(4, "0");
    return `${sp}      ${startTc} ${endTc}   ${fileName}`;
}

/**
 * @param {object} options
 * @param {Array<{start: number, end: number}>} options.events
 * @param {string} options.scriptName
 * @param {string} options.imagePrefix
 * @param {string} options.targetFolderPath
 * @param {string} options.resolutionId
 * @param {boolean} options.dropFrame
 * @param {(seconds: number) => string} options.formatTc
 * @param {number} [options.incode]
 * @param {string} [options.fillColor]
 * @param {string} [options.outlineColor]
 * @param {Date|string|number} [options.createdAt]
 * @param {string} [options.author]
 * @returns {string}
 */
export function generateSstText({
    events = [],
    scriptName,
    imagePrefix,
    targetFolderPath,
    resolutionId,
    dropFrame,
    formatTc,
    incode = 0,
    fillColor,
    outlineColor,
    createdAt = new Date(),
    author = "Closed Caption Creator",
}) {
    if (typeof formatTc !== "function") {
        throw new Error("generateSstText requires a formatTc function");
    }

    const stem = getScriptStem(scriptName);
    const fileName = ensureScriptExtension(scriptName, "sst");
    const { tvType, pixelArea, displayArea } = displayGeometry(resolutionId);
    const tapeType = dropFrame ? "DROP" : "NON_DROP";
    const timeCodeLabel = dropFrame ? "DropFrame" : "Non DropFrame";
    const planes = resolveScenaristPlanes(fillColor, outlineColor);
    const incodeSec = Number(incode) || 0;
    const baseTime = formatTc(incodeSec);
    const directory = String(targetFolderPath || "").trim() || ".";

    const lines = [
        "st_format 2",
        "################################################################",
        "# Daikin Scenarist Script",
        "#---------------------------------------------------------------",
        `# Filename:   ${fileName}`,
        `# Created:    ${formatCreated(createdAt)}`,
        `# Time Code:  ${timeCodeLabel}`,
        `# Video Mode: ${tvType}`,
        `# Author:     ${author}`,
        "################################################################",
        "",
        headerField("Subtitle", stem),
        headerField("TV_Type", tvType),
        headerField("Tape_Type", tapeType),
        headerField("Base_Time", baseTime),
        "# Display_Start .... forced/non_forced",
        headerField("Display_Start", "non_forced"),
        headerField("Pixel_Area", pixelArea),
        headerField("Display_Area", displayArea),
        "# Color index value .... (E2 E1 Pa Bg) (1-16)",
        headerField("Color", "(0 4 3 13)"),
        "# Contrast .... (E2 E1 Pa Bg) (1-15)",
        headerField("Contrast", "(14 15 15 15)"),
        "# Conversion rule .... (R G B) (0-255)",
        conversionLine("BG", planes.BG),
        conversionLine("PA", planes.PA),
        conversionLine("E1", planes.E1),
        conversionLine("E2", planes.E2),
        "# Image file directory",
        headerField("Directory", directory),
        "###############################################",
        "SP_NUMBER     START       END       FILE_NAME",
    ];

    events.forEach((event, index) => {
        const startTc = formatTc((Number(event.start) || 0) + incodeSec);
        const endTc = formatTc((Number(event.end) || 0) + incodeSec);
        const imageFileName = getImageFileName(imagePrefix, index, {
            extension: "tif",
            separator: ".",
        });
        lines.push(formatEventRow(index, startTc, endTc, imageFileName));
    });

    return lines.join("\r\n") + "\r\n";
}

export function generateSst(options) {
    return encodeUtf16Be(generateSstText(options));
}
